import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import React, { memo } from 'react'

import { Props as ServiceProps, Service } from './Service'

export interface Props {
  description?: string
  image?: ImageProps
  label?: string
  services: ServiceProps[]
  title?: string
}

export const ServicesPreview = memo(function ServicesPreview({
  description,
  image,
  label,
  services,
  title,
}: Props) {
  if (!services || services.length < 1) {
    return null
  }

  return (
    <Container>
      <Head>
        {label ? <Label tag="h2" text={label} /> : null}

        {title ? <Title tag="h3" text={title} /> : null}

        {description ? (
          <FadeInUp>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}
      </Head>

      <Wrapper>
        {image ? <Image {...image} /> : null}

        <List row wrap>
          {services.slice(0, 3).map((item, index) => (
            <Service key={index} index={index} {...item} />
          ))}
        </List>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  margin-top: 11.25rem;

  @media (max-width: 1199px) {
    margin-top: 5.25rem;
  }
`

const Head = styled.div`
  text-align: center;

  @media (max-width: 1199px) {
    padding: 0 2rem;
  }
`

const Label = styled(AnimatedTitle)`
  ${styles.label}
`

const Title = styled(AnimatedTitle)`
  ${styles.title.large}
`

const Description = styled.div`
  ${styles.description}

  font-size: 0.875rem;
  line-height: 1.5rem;
  max-width: 49rem;
  margin: 1.5rem auto 0;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Wrapper = styled.div`
  background: ${({ theme }) => theme.colors.variants.primaryDark};
  margin-top: 6rem;
  overflow: hidden;
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralDark1};
    opacity: 0.5;
    pointer-events: none;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
  }

  > picture {
    img {
      ${styles.img}
    }
  }

  @media (max-width: 1199px) {
    background: none;
    margin-top: 3.6875rem;
    &:before,
    > picture {
      display: none;
    }
  }
`

const List = styled(FlexBox)`
  &:before,
  &:after {
    content: '';
    width: 0.0625rem;
    height: 100%;
    background: ${({ theme }) => theme.colors.variants.neutralLight1};
    opacity: 0.15;
    pointer-events: none;
    position: absolute;
    top: 0;
    z-index: 2;
  }
  &:before {
    left: 33.333%;
  }
  &:after {
    right: 33.333%;
  }

  @media (max-width: 1199px) {
    display: block;
    &:before,
    &:after {
      display: none;
    }
  }
`
